import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import './LoginPage.css';

const MIN_PASSWORD_LENGTH = 8;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setError('Invalid reset link. Please request a new password reset.');
      setIsVerifying(false);
      return;
    }
    setToken(t);

    // Verify token with backend to give immediate feedback
    const verify = async () => {
      try {
        const result = await authService.verifyResetToken(t);
        if (!result.valid) {
          setError(result.message || 'This password reset link is invalid or has expired.');
          setIsValidToken(false);
        } else {
          setIsValidToken(true);
        }
      } catch (e) {
        setError('Unable to verify reset link. Please try again later.');
      } finally {
        setIsVerifying(false);
      }
    };

    verify();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setError('');
    setMessage('');

    if (!newPassword || newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      const respMessage = await authService.resetPassword(token, newPassword);
      setMessage(respMessage || 'Your password has been reset successfully.');
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.detail ||
        'We were unable to reset your password. The link may have expired. Please request a new reset link.';
      setError(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="logo-icon">
            <img
              src="/images/zivo_logo.png"
              alt="Zivo Logo"
              className="logo-image"
            />
          </div>
          <h2 className="login-title">Reset your password</h2>
          <p className="login-subtitle">
            Enter a new password for your ZivoHealth account.
          </p>
        </div>

        <div className="login-form-container">
          {isVerifying ? (
            <div className="info-message">Verifying your reset link…</div>
          ) : !isValidToken ? (
            <div className="error-message">
              {error || 'This password reset link is invalid or has expired.'}
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="login-button"
                  onClick={() => navigate('/forgot-password')}
                >
                  Request new reset link
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                  disabled={submitting}
                />
              </div>

              {error && <div className="error-message">{error}</div>}
              {message && !error && <div className="info-message">{message}</div>}

              <button
                type="submit"
                className="login-button"
                disabled={submitting}
              >
                {submitting ? 'Resetting…' : 'Reset Password'}
              </button>

              <div className="signup-link" style={{ marginTop: 16 }}>
                <span>Remember your password? </span>
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => navigate('/login')}
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;


